import { useState } from 'react';
import { Phone, Mail, MapPin, CheckCircle, AlertCircle } from 'lucide-react';

// Service type options for dropdown
const SERVICE_TYPES = [
  { value: '', label: 'Select a service type...' },
  { value: 'construction', label: 'Construction' },
  { value: 'facility-maintenance', label: 'Facility Maintenance' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'emergency-response', label: 'Emergency Response' },
  { value: 'base-operations', label: 'Base Operations Support' },
  { value: 'other', label: 'Other' },
];

// Primary phone number - standardized across site
const PRIMARY_PHONE = '(918) 493-3418';
const PRIMARY_PHONE_TEL = '9184933418';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    // Honeypot field for spam protection
    website: '', // This should remain empty - bots will fill it
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if website field is filled, it's likely a bot
    if (formData.website) {
      // Silently reject but show success to not alert the bot
      setSubmitStatus('success');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          serviceType: formData.serviceType,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          company: '',
          email: '', 
          phone: '', 
          serviceType: '',
          message: '',
          website: '',
        });
        setErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  return (
    <div className="pt-28">
      {/* Urgency Banner */}
      <section className="bg-gradient-to-r from-gold to-gold-dark py-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-black font-bold text-sm md:text-base uppercase tracking-wider">
            Need Rapid Deployment? Contact Us 24/7 for Emergency Response &amp; Critical Projects
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">24/7 Availability</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready When <span className="text-gold">You Are</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto mb-8">
            Emergency response. Rapid deployment. Mission-critical support nationwide and worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href={`tel:${PRIMARY_PHONE_TEL}`}
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
              data-testid="hero-call-button"
            >
              Call Now: {PRIMARY_PHONE}
            </a>
            <a 
              href="mailto:inquire@williamsdiversified.com"
              className="inline-block bg-transparent border-2 border-gold hover:bg-gold hover:text-black text-gold font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30" data-testid="contact-section">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="pt-28">
                <h2 className="text-3xl font-bold text-white mb-10 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gold p-4 rounded-lg group-hover:scale-110 transition-transform">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div className="pt-28">
                      <p className="text-gray-400 text-sm mb-2 uppercase tracking-wide">Phone Number</p>
                      <a href={`tel:${PRIMARY_PHONE_TEL}`} className="text-white text-xl hover:text-gold transition-colors font-medium" data-testid="contact-phone">{PRIMARY_PHONE}</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gold p-4 rounded-lg group-hover:scale-110 transition-transform">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div className="pt-28">
                      <p className="text-gray-400 text-sm mb-2 uppercase tracking-wide">Email Address</p>
                      <a href="mailto:inquire@williamsdiversified.com" className="text-white text-xl hover:text-gold transition-colors font-medium" data-testid="contact-email">inquire@williamsdiversified.com</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group">
                    <div className="bg-gold p-4 rounded-lg group-hover:scale-110 transition-transform">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div className="pt-28">
                      <p className="text-gray-400 text-sm mb-2 uppercase tracking-wide">Headquarters</p>
                      <p className="text-white text-lg font-medium">1913 W Tacoma St, Suite A &amp; B</p>
                      <p className="text-white text-lg">Broken Arrow, OK 74012</p>
                      <p className="text-gold text-sm mt-2" data-testid="contact-location">Serving Clients Nationwide &amp; Worldwide</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black border border-gold/30 rounded-lg p-10">
              <h2 className="text-2xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Send Us a Message</h2>
              
              {submitStatus === 'success' ? (
                <div className="text-center py-12" data-testid="form-success">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-300 mb-6">Your message has been sent successfully. We&apos;ll be in touch within 24 hours.</p>
                  <button
                    onClick={() => setSubmitStatus(null)}
                    className="bg-gold hover:bg-gold-dark text-black font-bold py-3 px-8 rounded transition-all"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" noValidate>
                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <div className="absolute -left-[9999px]" aria-hidden="true">
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                        Name <span className="text-gold">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full bg-black border ${errors.name ? 'border-red-500' : 'border-gold/30'} rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors`}
                        data-testid="form-name"
                      />
                      {errors.name && (
                        <p className="text-red-400 text-sm mt-1 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" /> {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full bg-black border border-gold/30 rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors"
                        data-testid="form-company"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                        Email <span className="text-gold">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full bg-black border ${errors.email ? 'border-red-500' : 'border-gold/30'} rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors`}
                        data-testid="form-email"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" /> {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                        Phone <span className="text-gold">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full bg-black border ${errors.phone ? 'border-red-500' : 'border-gold/30'} rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors`}
                        data-testid="form-phone"
                      />
                      {errors.phone && (
                        <p className="text-red-400 text-sm mt-1 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" /> {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                      Type of Service Needed <span className="text-gold">*</span>
                    </label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      className={`w-full bg-black border ${errors.serviceType ? 'border-red-500' : 'border-gold/30'} rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold transition-colors`}
                      data-testid="form-service-type"
                    >
                      {SERVICE_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.serviceType && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.serviceType}
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-8">
                    <label className="block text-gray-300 text-sm mb-3 uppercase tracking-wide">
                      Message <span className="text-gold">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="5"
                      className={`w-full bg-black border ${errors.message ? 'border-red-500' : 'border-gold/30'} rounded-lg px-4 py-4 text-white focus:outline-none focus:border-gold resize-none transition-colors`}
                      placeholder="Tell us about your project..."
                      data-testid="form-message"
                    ></textarea>
                    {errors.message && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.message}
                      </p>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full ${isSubmitting ? 'bg-gray-500' : 'bg-gold hover:bg-gold-dark'} text-black font-bold py-5 rounded-lg transition-all transform hover:scale-105 uppercase tracking-wider text-lg`}
                    data-testid="form-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Message'}
                  </button>
                  
                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-400 text-center" data-testid="form-error">
                      Sorry, there was an error sending your message. Please try again or call us directly at {PRIMARY_PHONE}.
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Response CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gradient-to-r from-gold/10 to-transparent border-l-4 border-gold rounded-lg p-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="pt-28">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  Critical Project?<br />
                  <span className="text-gold">We Deploy Fast.</span>
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  When mission-critical operations are on the line, Williams Diversified responds with 48-hour nationwide deployment capability. Government installations, defense facilities, and commercial emergencies—we&apos;re ready 24/7.
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-gold mr-3 text-2xl">✓</span>
                    <span>24/7 emergency response hotline</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 text-2xl">✓</span>
                    <span>48-hour deployment capability nationwide</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 text-2xl">✓</span>
                    <span>Rapid mobilization for government &amp; defense</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 text-2xl">✓</span>
                    <span>Ready for federal facility projects</span>
                  </li>
                </ul>
              </div>
              <div className="bg-black border-2 border-gold rounded-lg p-8 text-center">
                <p className="text-gold text-sm font-bold uppercase tracking-wider mb-4">Emergency Hotline</p>
                <a 
                  href={`tel:${PRIMARY_PHONE_TEL}`}
                  className="text-white text-4xl md:text-5xl font-bold hover:text-gold transition-colors mb-6 block"
                  data-testid="emergency-phone"
                >
                  {PRIMARY_PHONE}
                </a>
                <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
                <p className="text-gold text-lg font-semibold mb-4">
                  Serving Clients Worldwide
                </p>
                <p className="text-gray-400 mb-6">
                  Available 24 hours a day, 7 days a week for emergency response and critical project consultation.
                </p>
                <a 
                  href="mailto:inquire@williamsdiversified.com"
                  className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-8 rounded text-base transition-all transform hover:scale-105 uppercase tracking-wider"
                >
                  Email for Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
